import { STAGE } from '../stage.js';
import { PLATFORM_MODELS } from './assets.generated.js';
import { WORLD_PACKAGE_LIMITS as L } from './format.js';
import { isPoint3 } from './shapes.js';
import { COLOUR, isNumber, isRecord, lookup, metres, problem, show } from './util.js';
const DEG = Math.PI / 180;
const FIELDS = [
    'model',
    'at',
    'tint',
    'instanced',
    'unit',
    'matte',
    'sway',
    'variant',
    'origin',
];
const PLACES = 64;
/**
 * The props' shape and names (V1): platform models that exist, their parts, at most 64 placements a
 * model and 400 in all. Lengths are checked before anything walks a list.
 */
export function propsProblems(value, path) {
    if (!Array.isArray(value) || value.length > L.props)
        return [problem(path, `moet een lijst van hooguit ${L.props} modellen zijn`)];
    const out = [];
    let placements = 0;
    value.forEach((prop, i) => {
        const at = `${path}/${i}`;
        if (!isRecord(prop))
            return void out.push(problem(at, `moet {${FIELDS.join(', ')}} zijn`));
        for (const name of FIELDS)
            if (!Object.hasOwn(prop, name) || prop[name] === undefined)
                out.push(problem(`${at}/${name}`, 'ontbreekt'));
        for (const name of Object.keys(prop))
            if (!FIELDS.includes(name))
                out.push(problem(`${at}/${show(name)}`, 'onbekend veld'));
        const model = lookup(PLATFORM_MODELS, prop['model']);
        if (!model)
            out.push(problem(`${at}/model`, `het platform heeft geen model "${show(prop['model'])}"`));
        else if (prop['variant'] !== null &&
            prop['variant'] !== undefined &&
            !model.variants.some((v) => v.name === prop['variant']))
            out.push(problem(`${at}/variant`, `"${show(prop['model'])}" heeft geen deel "${show(prop['variant'])}"`));
        const all = prop['at'];
        const valid = Array.isArray(all) && all.length >= 1 && all.length <= PLACES;
        if (!valid)
            out.push(problem(`${at}/at`, `moet 1 tot ${PLACES} plaatsingen {pos, turn, scale, tilt} hebben`));
        const places = valid ? all : [];
        placements += places.length;
        places.forEach((place, k) => {
            const ok = isRecord(place) &&
                Object.keys(place).length === 4 &&
                isPoint3(place['pos']) &&
                isNumber(place['turn']) &&
                Math.abs(place['turn']) <= 360 &&
                isNumber(place['scale']) &&
                place['scale'] >= 0.01 &&
                place['scale'] <= 100 &&
                Array.isArray(place['tilt']) &&
                place['tilt'].length === 2 &&
                place['tilt'].every((t) => isNumber(t) && Math.abs(t) <= 90);
            if (!ok)
                out.push(problem(`${at}/at/${k}`, 'moet {pos: [x, y, z], turn: −360–360, scale: 0,01–100, tilt: [x, z] tot 90} zijn'));
        });
        if (prop['tint'] !== undefined &&
            prop['tint'] !== null &&
            !(typeof prop['tint'] === 'string' && COLOUR.test(prop['tint'])))
            out.push(problem(`${at}/tint`, 'moet null of een kleur als "#a1b2c3" zijn'));
        for (const flag of ['instanced', 'matte'])
            if (prop[flag] !== undefined && typeof prop[flag] !== 'boolean')
                out.push(problem(`${at}/${flag}`, 'moet true of false zijn'));
        if (prop['unit'] !== undefined &&
            (!isNumber(prop['unit']) || prop['unit'] <= 0 || prop['unit'] > 100))
            out.push(problem(`${at}/unit`, 'moet een getal boven 0 zijn, hooguit 100'));
        if (prop['sway'] !== undefined &&
            (!isNumber(prop['sway']) || prop['sway'] < 0 || prop['sway'] > 0.5))
            out.push(problem(`${at}/sway`, 'moet tussen 0 en 0,5 liggen'));
        if (prop['variant'] !== undefined &&
            prop['variant'] !== null &&
            typeof prop['variant'] !== 'string')
            out.push(problem(`${at}/variant`, 'moet null of een naam zijn'));
        if (prop['origin'] !== undefined &&
            prop['origin'] !== 'model' &&
            prop['origin'] !== 'footprint')
            out.push(problem(`${at}/origin`, 'moet model of footprint zijn'));
    });
    if (placements > L.propPlacements)
        out.push(problem(path, `samen hooguit ${L.propPlacements} plaatsingen`));
    return out;
}
/**
 * A placement's corners as world.ts placementMatrix places them (Euler YXZ: tilt about x, turn about y,
 * tilt about z; then scale · unit), the box around them. apps/headset's package-facts.test.ts holds it to three.
 */
export function propBox(prop, place) {
    const model = lookup(PLATFORM_MODELS, prop.model);
    if (!model)
        return undefined;
    const variant = prop.variant ? model.variants.find((v) => v.name === prop.variant) : undefined;
    const size = variant?.size ?? model.bounds.max.map((v, i) => v - model.bounds.min[i]);
    const b = prop.origin === 'footprint'
        ? { min: [-size[0] / 2, 0, -size[2] / 2], max: [size[0] / 2, size[1], size[2] / 2] }
        : model.bounds;
    const [ax, az] = [place.tilt[0] * DEG, place.tilt[1] * DEG];
    const ay = place.turn * DEG;
    const s = place.scale * prop.unit;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const x of [b.min[0], b.max[0]])
        for (const y of [b.min[1], b.max[1]])
            for (const z of [b.min[2], b.max[2]]) {
                // R = Ry · Rx · Rz (three's 'YXZ').
                const x1 = x * Math.cos(az) - y * Math.sin(az);
                const y1 = x * Math.sin(az) + y * Math.cos(az);
                const y2 = y1 * Math.cos(ax) - z * Math.sin(ax);
                const z2 = y1 * Math.sin(ax) + z * Math.cos(ax);
                const x3 = x1 * Math.cos(ay) + z2 * Math.sin(ay);
                const z3 = -x1 * Math.sin(ay) + z2 * Math.cos(ay);
                const p = [x3 * s + place.pos[0], y2 * s + place.pos[1], z3 * s + place.pos[2]];
                for (let k = 0; k < 3; k++) {
                    min[k] = Math.min(min[k], p[k]);
                    max[k] = Math.max(max[k], p[k]);
                }
            }
    return { min, max };
}
/** Props out of the clear cylinder round the standing area (ADR-061), as meshes are. */
export function propStageProblems(props, path) {
    const out = [];
    props.forEach((prop, i) => prop.at.forEach((place, k) => {
        const box = propBox(prop, place);
        if (!box)
            return;
        const dx = Math.max(box.min[0], 0, -box.max[0]);
        const dz = Math.max(box.min[2], 0, -box.max[2]);
        if (Math.hypot(dx, dz) < STAGE.clear &&
            box.max[1] > STAGE.apronHeight &&
            box.min[1] < STAGE.clearHeight)
            out.push(problem(`${path}/${i}/at/${k}`, `"${prop.model}" staat in de vrije ruimte rond het podium (hoger dan 5 cm en lager dan ${metres(STAGE.clearHeight)} m, binnen ${metres(STAGE.clear)} m)`));
    }));
    return out;
}
const mipmapped = (w, h) => Math.round(w * h * 4 * (4 / 3));
/** What one prop costs, as budget.ts counts a code world's (V3). */
export function propCost(prop) {
    const model = lookup(PLATFORM_MODELS, prop.model);
    if (!model)
        return { triangles: 0, drawCalls: 0, textureBytes: 0, bytes: 0 };
    const variant = prop.variant ? model.variants.find((v) => v.name === prop.variant) : undefined;
    const copies = prop.at.length;
    return {
        triangles: (variant?.triangles ?? model.triangles) * copies,
        drawCalls: prop.instanced ? model.materials : model.materials * copies,
        textureBytes: model.textures.reduce((sum, t) => sum + mipmapped(t.width, t.height), 0),
        bytes: model.bytes,
    };
}
//# sourceMappingURL=props.js.map